import { Button } from '@/components/ui/Button';
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/Dialog';
import GMap from '@/components/ui/GMap';
import { Input } from '@/components/ui/Input';
import { Label } from '@/components/ui/Label';
import {
  Select,
  SelectContent,
  SelectGroup,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/Select';
import { DEFAULT_SITE_CONFIG } from '@/constants/config.constants';
import {
  AuditLogInsertInput,
  Customer,
  SiteInsertInput,
} from '@/generated/graphql';
import { execute } from '@/graphql/execute';
import { AuditLogAction, AuditLogEntityType } from '@/models/audit-log.models';
import { DEFAULT_UUID } from '@/models/constants';
import { createAuditLogInput } from '@/utils/audit-log.function';
import { DialogDescription } from '@radix-ui/react-dialog';
import Form from '@rjsf/core';
import { RJSFSchema } from '@rjsf/utils';
import { useMutation } from '@tanstack/react-query';
import { useRef } from 'react';
import { Controller, useForm } from 'react-hook-form';
import { useNavigate } from 'react-router';
import { toast } from 'sonner';
import { v4 as uuid } from 'uuid';
import { GeoJSONGeometry } from 'wellknown';
import { createSiteMutation } from '../../Customers/graphql/mutation';
import SiteConfigForm from './SiteConfigForm';

type CreateSiteFormData = {
  id?: string;
  name: string;
  customerId: string;
  geometry?: GeoJSONGeometry;
};
interface AddSiteDialogProps {
  open: boolean;
  customers: Customer[];
  onToggleOpen: () => void;
}

export function AddSiteDialog({
  open,
  customers,
  onToggleOpen,
}: AddSiteDialogProps) {
  const {
    register,
    getValues,
    control,
    formState: { isValid },
  } = useForm<CreateSiteFormData>({
    defaultValues: {
      name: '',
      customerId: customers[0]?.id,
    },
  });
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const configFormRef = useRef<Form<any, RJSFSchema, any>>(null);

  const navigate = useNavigate();
  const { isPending, mutateAsync: createSite } = useMutation({
    mutationKey: ['CreateSite'],
    mutationFn: ({
      inputs,
      auditLogInput,
    }: {
      inputs: SiteInsertInput;
      auditLogInput: AuditLogInsertInput;
    }) => {
      return execute({
        query: createSiteMutation,
        variables: {
          inputs,
          auditLogInput,
        },
      });
    },
    onSuccess: async (data) => {
      onToggleOpen();
      await navigate(`/sites/${data.insertSiteOne?.id}`);
      toast.success('Site created successfully!', {
        duration: 3000,
      });
    },
  });

  const handleToggleOpenState = () => {
    onToggleOpen();
  };

  const handleSubmit = async () => {
    const timestamp = new Date().toISOString();
    const config = configFormRef.current?.state.formData || {};
    const inputs: SiteInsertInput = {
      ...getValues(),
      id: uuid(),
      config,
      rowVersion: DEFAULT_UUID,
      createdAt: timestamp,
      updatedAt: timestamp,
    };

    await createSite({
      inputs,
      auditLogInput: createAuditLogInput({
        action: AuditLogAction.CREATE_SITE,
        entities: [
          {
            id: inputs.id as string,
            entity: AuditLogEntityType.SITE,
          },
        ],
      }),
    });
  };

  return (
    <Dialog open={open} onOpenChange={handleToggleOpenState}>
      <DialogContent className="max-h-[90dvh] sm:max-w-[80vw] lg:max-w-[80vw]">
        <DialogHeader>
          <DialogTitle>Add Site</DialogTitle>
          <DialogDescription />
        </DialogHeader>
        <div className="grid max-h-[70dvh] grid-cols-1 gap-4 overflow-auto px-1">
          <div className="grid gap-6">
            <div className="grid gap-2">
              <Label htmlFor="name">Name</Label>
              <Input
                type="text"
                id="name"
                placeholder="Name"
                {...register('name', {
                  required: 'Name is required',
                })}
              />
            </div>

            <div className="grid gap-2">
              <Label htmlFor="customer">Customer</Label>
              <Controller
                name="customerId"
                control={control}
                rules={{ required: true }}
                render={({ field }) => (
                  <Select onValueChange={field.onChange} value={field.value}>
                    <SelectTrigger className="w-full">
                      <SelectValue placeholder="Filter by customer" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectGroup>
                        {customers.map((customer) => (
                          <SelectItem value={customer.id} key={customer.id}>
                            {customer.name}
                          </SelectItem>
                        ))}
                      </SelectGroup>
                    </SelectContent>
                  </Select>
                )}
              />
            </div>

            <SiteConfigForm
              ref={configFormRef}
              formData={DEFAULT_SITE_CONFIG}
            />
          </div>
          <div className="col-span-1 grid gap-2">
            <div className="grid gap-2">
              <Controller
                name="geometry"
                control={control}
                rules={{ required: 'Location is required' }}
                render={({ field }) => (
                  <div className="flex flex-1 flex-col gap-2">
                    <Label>Location</Label>

                    <div className="flex-1">
                      <GMap
                        draw={true}
                        editingGeometry={field.value}
                        onEditGeometryChange={field.onChange}
                      />
                    </div>
                  </div>
                )}
              />
            </div>
          </div>
        </div>
        <DialogFooter>
          <Button
            type="button"
            className="md:w-[150px]"
            disabled={!isValid || isPending}
            onClick={() => void handleSubmit()}
          >
            Create
          </Button>
          <Button
            type="button"
            variant={'outline'}
            className="md:w-[150px]"
            onClick={() => onToggleOpen()}
          >
            Cancel
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
